import { useEffect, useState } from 'react'

import { useCameraBackgroundState } from '../../../hooks/useCameraBackgroundState'
import { useLiveKitMediaStatus } from '../../../hooks/useLiveKitMediaStatus'
import { useSelectedBackgroundEffect } from '../../../hooks/useSelectedBackgroundEffect'
import {
  getLiveKitMediaStatus,
  subscribeLiveKitMediaStatus,
} from '../../../services/liveKitMediaStatusStore'
import {
  backgroundProblemHoldsCamera,
  cameraWaitingForBackground,
  isBackgroundEffectActive,
  type BackgroundProblem,
} from '../../../services/liveKitVideoBackground'
import { liveCameraTrack } from '../cameraOffPicture'
import {
  type CoverPhase,
  useCoverUntilPainted,
  useEffectStartGap,
} from './CameraWaitingPlaceholder'

/**
 * SC-158: what a camera tile shows - picture, waiting cover or a camera-off box - decided once for the
 * overlay tile, the sidebar tile and the picture-in-picture tile, so the three can never disagree about
 * whether a held camera is shown.
 *
 * Every source here is read from the opener's own `window` and module state, never from the PiP window's
 * `defaultView`: the connector publishes to the opener, and a listener on the other window would hear
 * nothing.
 */

/** The camera a tile shows, as far as a change of it matters to its first paint. */
function pictureSource(trackRef: any): unknown {
  return trackRef?.publication?.track ?? trackRef?.publication ?? trackRef ?? null
}

/** Whether a remote participant said their camera is waiting for its background. */
function useRemoteCameraWaiting(participantId: string, isLocal: boolean): boolean {
  const status = useLiveKitMediaStatus(participantId)
  return !isLocal && (status?.cameraWaiting ?? false)
}

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}

/**
 * {@link useRemoteCameraWaiting} for a whole set of people at once: the picture-in-picture model is built
 * for everyone in one place, and a hook cannot be called once per person there.
 */
export function useRemoteCameraWaitingIds(ids: readonly string[]): ReadonlySet<string> {
  // Joined, so a new array holding the same people does not resubscribe on every proximity tick.
  const key = ids.join('\n')
  const [waiting, setWaiting] = useState<ReadonlySet<string>>(() => readWaitingIds(ids))

  useEffect(() => {
    const current = key ? key.split('\n') : []
    const update = () =>
      setWaiting((prev) => {
        const next = readWaitingIds(current)
        return sameIds(prev, next) ? prev : next
      })
    // Anything that changed between the first render and these subscriptions.
    update()
    const unsubscribes = current.map((id) => subscribeLiveKitMediaStatus(id, update))
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe())
  }, [key])

  return waiting
}

function readWaitingIds(ids: readonly string[]): ReadonlySet<string> {
  return new Set(ids.filter((id) => getLiveKitMediaStatus(id)?.cameraWaiting === true))
}

/**
 * Whether our own camera is on but waiting for its background: held while the
 * effect gets ready, or kept down by a problem with it. The same reading as the
 * camera button and our nameplate, so the three never disagree. Only while the
 * camera is wanted on: one turned off says so, whatever its start was doing.
 *
 * `problem` only when it is what keeps the camera down. A slow device or a
 * failed switch still shows an effect, so a hold beside one is a start that
 * will finish, not a failure.
 */
export function useLocalCameraWaiting(
  isLocal: boolean,
  videoEnabled: boolean
): { waiting: boolean; problem: BackgroundProblem | null; effectChosen: boolean } {
  // Remote tiles are told only that a camera waits (F8), never why, so they have no use for it.
  const state = useCameraBackgroundState({ enabled: isLocal })
  // Only our own tile shows our background; remote tiles skip the storage read
  // and the listener, and are not re-rendered by our effect changes.
  const [effect] = useSelectedBackgroundEffect({ enabled: isLocal })
  const waiting = isLocal && videoEnabled && cameraWaitingForBackground(state)
  const problem = waiting && backgroundProblemHoldsCamera(state.problem) ? state.problem : null
  const effectChosen = isLocal && videoEnabled && isBackgroundEffectActive(effect)
  return { waiting, problem, effectChosen }
}

export interface CameraTilePicture {
  /** The camera track the tile may show: none for our own camera turned off. */
  videoTrack: any
  /** Ours or theirs: the camera is on but held for its background. */
  waiting: boolean
  /** Ours only, and only when it is what keeps the camera down. */
  problem: BackgroundProblem | null
  /** The video mounts exactly when this says so. */
  showsPicture: boolean
  /** The waiting cover is up, with nothing beneath it allowed to show. */
  covered: boolean
  /** Set while the cover stays over a video that has not painted yet; see `useCoverUntilPainted`. */
  coverPhase: CoverPhase | null
  videoRef: (element: HTMLVideoElement | null) => void
}

/**
 * The picture decision every camera tile makes. `cameraTrack` is the room-filtered track reference for this
 * participant (never `useTracks()`, which returns every subscribed track in the LiveKit room).
 */
export function useCameraTilePicture({
  participantId,
  isLocal,
  videoEnabled,
  cameraTrack,
  participant,
}: {
  participantId: string
  isLocal: boolean
  /** What we asked our own camera to do; ignored for someone else's. */
  videoEnabled: boolean
  cameraTrack: any
  /**
   * The LiveKit participant, where the tile has one: a camera it reports off is never drawn (SC-270). The
   * picture-in-picture tile has none; its model has already decided that.
   */
  participant?: { isCameraEnabled?: boolean }
}): CameraTilePicture {
  const { waiting: localWaiting, problem, effectChosen } = useLocalCameraWaiting(
    isLocal,
    videoEnabled
  )
  const remoteWaiting = useRemoteCameraWaiting(participantId, isLocal)
  const waiting = localWaiting || remoteWaiting
  const shouldRenderLocalCamera = !isLocal || videoEnabled
  const videoTrack = shouldRenderLocalCamera ? cameraTrack : undefined
  // Our own camera is covered while it waits: what it would show is not ready. `pauseUpstream` does not
  // mute our own publication, so the track alone would still say "live". A muted track (SC-158 holds a
  // remote one off the wire too) renders as a black rectangle, so `liveCameraTrack` refuses it and the tile
  // shows the camera-off picture instead.
  const showsPicture =
    Boolean(liveCameraTrack(cameraTrack, isLocal, videoEnabled, participant)) && !localWaiting
  // With an effect chosen, our camera that is on but has no picture yet may
  // still be starting: its hold can end before LiveKit has published the track.
  // Covered as waiting for that gap only, not for a camera that has died.
  const startGap = useEffectStartGap({
    cameraOn: isLocal && videoEnabled,
    effectChosen,
    waiting: localWaiting,
    showsPicture,
  })
  const covered = showsPicture ? false : waiting || startGap
  const { coverPhase, videoRef } = useCoverUntilPainted(
    showsPicture,
    covered,
    pictureSource(videoTrack)
  )

  return {
    videoTrack,
    waiting,
    problem,
    showsPicture,
    covered,
    coverPhase,
    videoRef,
  }
}
